import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useContentType, useCreateContentItem } from '@wordpuppi/shared';
import type { ContentField, FieldType } from '@wordpuppi/shared';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/LoadingSkeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TypeIcon } from '@/components/TypeIcon';
import { ArrowLeft } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/sites/$siteSlug/content-types/tables/$typeSlug/new')({
  component: NewItemPage,
});

function NewItemPage() {
  const { siteSlug, typeSlug } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useContentType(siteSlug, typeSlug);
  const createItem = useCreateContentItem(siteSlug, typeSlug);
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const contentType = data?.data;
  const hasError = error || !contentType || 'error' in contentType;

  if (hasError) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Table not found</h2>
        <p className="text-muted-foreground mb-4">The table "{typeSlug}" doesn't exist.</p>
        <Button asChild>
          <Link to="/sites/$siteSlug/content-types/tables" params={{ siteSlug }}>
            Back to Tables
          </Link>
        </Button>
      </div>
    );
  }

  // Filter to only editable fields (exclude system fields like id, created_at, etc.)
  const editableFields = (contentType.fields || []).filter(
    (field) => !['id', 'site_id', 'created_at', 'updated_at', 'deleted_at', 'author_id'].includes(field.slug)
  );

  const handleFieldChange = (field: ContentField, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field.slug]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    for (const field of editableFields) {
      if (field.required && !formData[field.slug]) {
        toast.error(`${field.name} is required`);
        return;
      }
    }

    try {
      await createItem.mutateAsync(formData);
      toast.success('Item created successfully');
      navigate({
        to: '/sites/$siteSlug/content-types/tables/$typeSlug',
        params: { siteSlug, typeSlug },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create item');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <Link
          to="/sites/$siteSlug/content-types/tables/$typeSlug"
          params={{ siteSlug, typeSlug }}
          className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Back to {contentType.name}
        </Link>
        <h1 className="text-2xl font-semibold mt-2 flex items-center gap-2">
          <TypeIcon icon={contentType.icon} className="size-6 text-muted-foreground" />
          New {contentType.name}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="max-w-xl space-y-6">
        {editableFields.map((field) => (
          <FieldInput
            key={field.id}
            field={field}
            value={formData[field.slug]}
            onChange={(value) => handleFieldChange(field, value)}
          />
        ))}

        <div className="flex gap-4 pt-6 border-t">
          <Button asChild variant="secondary">
            <Link
              to="/sites/$siteSlug/content-types/tables/$typeSlug"
              params={{ siteSlug, typeSlug }}
            >
              Cancel
            </Link>
          </Button>
          <Button type="submit" disabled={createItem.isPending}>
            {createItem.isPending ? 'Creating...' : 'Create Item'}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface FieldInputProps {
  field: ContentField;
  value: unknown;
  onChange: (value: unknown) => void;
}

function FieldInput({ field, value, onChange }: FieldInputProps) {
  const renderInput = () => {
    switch (field.field_type as FieldType) {
      case 'text':
      case 'slug':
      case 'url':
      case 'email':
        return (
          <Input
            type={field.field_type === 'email' ? 'email' : field.field_type === 'url' ? 'url' : 'text'}
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${field.name.toLowerCase()}`}
          />
        );

      case 'long_text':
      case 'rich_text':
        return (
          <Textarea
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${field.name.toLowerCase()}`}
            className="min-h-32"
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={(value as number) ?? ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
            placeholder={`Enter ${field.name.toLowerCase()}`}
          />
        );

      case 'boolean':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={(value as boolean) || false}
              onChange={(e) => onChange(e.target.checked)}
              className="rounded border-input"
            />
            {field.name}
          </label>
        );

      case 'date':
        return (
          <Input
            type="date"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 'date_time':
        return (
          <Input
            type="datetime-local"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );

      case 'enum':
        const enumValues = field.options?.enum_values || [];
        return (
          <Select
            value={(value as string) || ''}
            onValueChange={(v: string) => onChange(v)}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${field.name.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {enumValues.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'json':
        return (
          <Textarea
            value={typeof value === 'string' ? value : JSON.stringify(value || {}, null, 2)}
            onChange={(e) => {
              try {
                onChange(JSON.parse(e.target.value));
              } catch {
                // Keep as string if not valid JSON
                onChange(e.target.value);
              }
            }}
            placeholder={`Enter valid JSON`}
            className="min-h-32 font-mono"
          />
        );

      case 'image':
      case 'file':
        return (
          <Input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${field.field_type} URL or path`}
          />
        );

      case 'relation':
        return (
          <Input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter related item ID`}
          />
        );

      default:
        return (
          <Input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={`Enter ${field.name.toLowerCase()}`}
          />
        );
    }
  };

  // For boolean fields, the label is inside the checkbox
  if (field.field_type === 'boolean') {
    return (
      <div className="space-y-2">
        {renderInput()}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>
        {field.name}
        {field.required && <span className="text-destructive ml-1">*</span>}
        <span className="text-xs text-muted-foreground font-normal ml-2">
          {field.field_type}
        </span>
      </Label>
      {renderInput()}
    </div>
  );
}
